import React, {useLayoutEffect, useRef} from 'react'
import POSSIBLE_CHECKOUTS from "../../../../util/PossibleCheckouts";
// Schrift selbst gehostet (kein Abruf bei Google), nur die genutzten Stärken
import "@fontsource/barlow-semi-condensed/600.css";
import "@fontsource/barlow-semi-condensed/700.css";
import "./liveTVScoreboard.css";

const MAX_FINISH = 170;
const STANDARD_STARTPUNKTE = 501;
// kleinste Schrift für Namen, darunter wird mit "…" gekürzt
const MIN_NAME_PX = 11;

/**
 * Moderne TV-Anzeigetafel für ein Spiel am Board: Namen oben, Restpunkte in Kreisen mit Fortschrittsbogen,
 * in der Mitte Modus und Legs/Sets, daneben die Finishwege. Das aktive Spiel markiert der rote Namensreiter.
 */
export default function LiveTVScoreboard({game, boardName, checkedPlayerIds, showAverage, showLastThrow, showDarts, showFinish = true, showBoard = true, onClick}) {

    if (game === undefined) {
        return <div className="lvtv">
            <div className="lvtv-tafel lvtv-tafel-leer">Am Board wird aktuell nicht gespielt.</div>
            <Fusszeile boardName={showBoard ? boardName : undefined}/>
        </div>
    }

    const istEDart = game.dartType === "EDART";
    const istSetModus = game.sets > 1;
    const abgeschlossen = !!game.abgeschlossen;

    const spieler = [1, 2].map(nr => {
        const id = game["spieler" + nr + "Id"];
        const sets = game["spieler" + nr + "Sets"];
        const legs = game["spieler" + nr + "Legs"];
        const gegnerSets = game["spieler" + (3 - nr) + "Sets"];
        const gegnerLegs = game["spieler" + (3 - nr) + "Legs"];
        return {
            nr, id, sets, legs,
            name: game["spieler" + nr + "Name"],
            togo: game["spieler" + nr + "Togo"],
            startpunkte: game["spieler" + nr + "Startpunkte"] || game.startpunkte || STANDARD_STARTPUNKTE,
            average: game["spieler" + nr + "Average"],
            lastThrow: game["spieler" + nr + "LastThrow"],
            darts: anzahlDarts(game["legSpieler" + nr]),
            aktiv: !abgeschlossen && id === game.spielerIdActive,
            anwurf: !abgeschlossen && id === game.spielerIdLegAnwurf,
            gewinner: abgeschlossen && (istSetModus ? sets > gegnerSets : legs > gegnerLegs),
            checked: checkedPlayerIds.includes(id),
            kannChecken: !abgeschlossen && !!game["canCheckSpieler" + nr],
        };
    });

    const status = game.startssoon ? "Spiel startet …" : game.inGame ? "Spieler im Spiel …" : abgeschlossen ? "Spiel beendet" : undefined;

    return <div className={"lvtv" + (onClick ? " lvtv-klickbar" : "")} onClick={onClick}>
        <div className="lvtv-reiter">
            {spieler.map(s => <div key={s.nr} className={"lvtv-name" + (s.aktiv || s.gewinner ? " lvtv-name-aktiv" : "") + (abgeschlossen && !s.gewinner ? " lvtv-name-verloren" : "")}>
                <PassenderName name={s.name}/>
            </div>)}
        </div>
        <div className="lvtv-zeile">
            <Finishwege spieler={spieler[0]} istEDart={istEDart} anzeigen={showFinish} seite="links"/>
            <div className="lvtv-tafel">
                <Punkte spieler={spieler[0]} seite="links" istEDart={istEDart} zusatz={zusatzwerte(spieler[0], showAverage, showLastThrow, showDarts && !istEDart)}/>
                <div className="lvtv-mitte">
                    {status ? <div className="lvtv-status">{status}</div> : <div className="lvtv-modus">{modusText(game)}</div>}
                    {istSetModus && <div className="lvtv-stand lvtv-stand-klein">
                        <span>{spieler[0].sets}</span><span className="lvtv-stand-label">Sets</span><span>{spieler[1].sets}</span>
                    </div>}
                    <div className="lvtv-stand">
                        <span>{spieler[0].legs}</span><span className="lvtv-stand-label">Legs</span><span>{spieler[1].legs}</span>
                    </div>
                </div>
                <Punkte spieler={spieler[1]} seite="rechts" istEDart={istEDart} zusatz={zusatzwerte(spieler[1], showAverage, showLastThrow, showDarts && !istEDart)}/>
            </div>
            <Finishwege spieler={spieler[1]} istEDart={istEDart} anzeigen={showFinish} seite="rechts"/>
        </div>
        <Fusszeile boardName={showBoard ? boardName : undefined}/>
        <Werbung imageUrl={game.werbeImageUrl} url={game.werbeUrl}/>
    </div>
}

function Punkte({spieler, seite, istEDart, zusatz}) {
    // Bogen: Anteil der noch zu werfenden Punkte am Startwert
    const radius = 42;
    const umfang = 2 * Math.PI * radius;
    const rest = Number(spieler.togo);
    const anteil = !istEDart && rest > 0 ? Math.min(rest / spieler.startpunkte, 1) : 0;

    return <div className="lvtv-punkte">
        {/* Anwurf im Leg: weißer Punkt in der äußeren oberen Ecke über dem Kreis */}
        {spieler.anwurf && <span className={"lvtv-anwurf lvtv-anwurf-" + seite} title="Anwurf im Leg" aria-label="Anwurf im Leg"/>}
        <div className="lvtv-kreis">
            <svg viewBox="0 0 100 100" className="lvtv-bogen" aria-hidden="true">
                <circle cx="50" cy="50" r={radius} className="lvtv-bogen-grund"/>
                {anteil > 0 && <circle cx="50" cy="50" r={radius} className="lvtv-bogen-wert"
                                       strokeDasharray={(anteil * umfang) + " " + umfang} transform="rotate(-90 50 50)"/>}
            </svg>
            {/* Rest in Grün, sobald ein Check möglich ist (wie in der Kachel-Ansicht) */}
            <span className={"lvtv-rest" + (spieler.checked ? " lvtv-rest-check" : !istEDart && spieler.kannChecken ? " lvtv-rest-checkbar" : "")}>
                {istEDart ? spieler.legs : spieler.checked ? "Check" : spieler.togo}
            </span>
            {spieler.aktiv && <Dart/>}
        </div>
        {/* Average, letzter Wurf und Darts untereinander */}
        {zusatz && <div className="lvtv-zusatz">{zusatz.map(wert => <span key={wert}>{wert}</span>)}</div>}
    </div>
}

function Finishwege({spieler, istEDart, anzeigen, seite}) {
    // auch ausgeblendet bleibt der Platz stehen, damit die Tafel nicht verrutscht
    const wege = !anzeigen || istEDart || spieler.checked ? [] : finishweg(Number(spieler.togo));
    return <div className={"lvtv-finish lvtv-finish-" + seite + (spieler.aktiv ? " lvtv-finish-aktiv" : "")} aria-label={wege.length > 0 ? "Finishweg " + wege.join(" ") : undefined}>
        {wege.map((feld, index) => <span key={index} className="lvtv-finish-feld">{feld}</span>)}
    </div>
}

/**
 * Name, dessen Schrift so weit verkleinert wird, bis er komplett in den Reiter passt. Ausgangsgröße ist die Schriftgröße
 * des Reiters aus dem CSS (auf Handys kleiner). Erst unterhalb der Mindestgröße wird mit "…" gekürzt.
 */
function PassenderName({name}) {
    const ref = useRef(null);

    useLayoutEffect(() => {
        const element = ref.current;
        if (!element || !element.parentElement) {
            return;
        }
        const anpassen = () => {
            const maxGroesse = parseFloat(window.getComputedStyle(element.parentElement).fontSize);
            const minGroesse = Math.min(MIN_NAME_PX, maxGroesse);
            let groesse = maxGroesse;
            element.style.fontSize = groesse + "px";
            // erst grob über das Verhältnis, dann in kleinen Schritten, bis nichts mehr übersteht
            if (element.scrollWidth > element.clientWidth && element.scrollWidth > 0) {
                groesse = Math.max(minGroesse, Math.floor(groesse * element.clientWidth / element.scrollWidth));
                element.style.fontSize = groesse + "px";
            }
            while (element.scrollWidth > element.clientWidth && groesse > minGroesse) {
                groesse -= 1;
                element.style.fontSize = groesse + "px";
            }
        };
        anpassen();
        // neu messen, wenn sich die Breite ändert oder die Schrift fertig geladen ist
        const beobachter = new ResizeObserver(() => anpassen());
        beobachter.observe(element.parentElement);
        let aktiv = true;
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => aktiv && anpassen());
        }
        return () => {
            aktiv = false;
            beobachter.disconnect();
        };
    }, [name]);

    return <span ref={ref} className="lvtv-name-text" title={name}>{name}</span>
}

// Werbebanner des Veranstalters unter der Tafel, wie in der Kachelansicht (LiveViewGame)
function Werbung({imageUrl, url}) {
    if (!vorhanden(imageUrl)) {
        return null;
    }
    const bild = <img src={imageUrl} alt="Werbung" className="lvtv-werbung-bild"/>;
    return <div className="lvtv-werbung">
        {/* der Klick auf die Werbung soll nicht die Spieldetails öffnen */}
        {vorhanden(url) ? <a href={url} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}>{bild}</a> : bild}
    </div>
}

// Fußzeile: Board links (sofern eingeblendet), Darthelfer-Logo rechts
function Fusszeile({boardName}) {
    return <div className="lvtv-fuss">
        <span className="lvtv-fuss-board">{boardName}</span>
        <img alt="Darthelfer" src="/images/darthelfer_quer_weiss.png" className="lvtv-logo"/>
    </div>
}

function Dart() {
    return <svg viewBox="0 0 24 24" className="lvtv-dart" aria-label="wirft">
        <path d="M3 21 L9 15" strokeWidth="2" strokeLinecap="round"/>
        <path d="M9 15 L16 8" strokeWidth="3.4" strokeLinecap="round"/>
        <path d="M16 8 L21 3" strokeWidth="1.2" strokeLinecap="round"/>
        <path d="M2 18 L6 18 L6 22" strokeWidth="1.4" fill="none" strokeLinejoin="round"/>
    </svg>
}

// Finishweg mit drei Darts aus der Finish-Tabelle (Double-Out), z.B. "T20 | T15 | D20" -> ["T20", "T15", "D20"]
function finishweg(rest) {
    if (!Number.isInteger(rest) || rest < 2 || rest > MAX_FINISH) {
        return [];
    }
    const weg = POSSIBLE_CHECKOUTS.SINGLE_IN.DOUBLE_OUT[3][rest];
    return weg ? weg.split("|").map(feld => feld.trim()).filter(feld => feld !== "") : [];
}

function anzahlDarts(leg) {
    if (leg && leg.scores) {
        return leg.scores.filter(tmp => tmp.value !== null && tmp.value !== undefined && tmp.value !== "").length * 3;
    }
    return 0;
}

function zusatzwerte(spieler, showAverage, showLastThrow, showDarts) {
    const werte = [];
    if (showLastThrow) {
        werte.push("Last " + (vorhanden(spieler.lastThrow) ? spieler.lastThrow : "-"));
    }
    if (showDarts) {
        werte.push(spieler.darts + " Darts");
    }
    // der Average steht ganz unten
    if (showAverage) {
        werte.push("Ø " + (vorhanden(spieler.average) ? spieler.average : "-"));
    }
    return werte.length > 0 ? werte : undefined;
}

// auch von der Live-Liste genutzt
export function modusText(game) {
    if (game.sets > 1) {
        return vorhanden(game.fistToLegs) ? "Sets " + game.sets + " · First to " + game.fistToLegs : "Sets " + game.sets + " · Best of " + game.bestOfLegs;
    }
    if (vorhanden(game.fistToLegs)) {
        return "First to " + game.fistToLegs;
    }
    if (vorhanden(game.bestOfLegs)) {
        return "Best of " + game.bestOfLegs;
    }
    return vorhanden(game.toLegs) ? game.toLegs + " Legs" : "";
}

function vorhanden(wert) {
    return wert !== null && wert !== undefined && wert !== "";
}
